import React, { useState, useEffect } from "react";
import Axios from "axios";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import JobFileTable from "../../components/Table/JobFileTable";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../../utils/axios";
import Swal from "sweetalert2";

export default function JobFilePage() {
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const initialSearch = queryParams.get("search") || "";
  const initialPage = Math.max(1, Number(queryParams.get("page")) || 1);
  const initialLimit = Math.max(1, Number(queryParams.get("limit")) || 100);
  const initialFocusedId = queryParams.get("focus") || null;
  const [search, setSearch] = useState(initialSearch);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);
  const [focusedJobId, setFocusedJobId] = useState(initialFocusedId);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("all");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearch(params.get("search") || "");
    setCurrentPage(Math.max(1, Number(params.get("page")) || 1));
    setItemsPerPage(Math.max(1, Number(params.get("limit")) || 100));
    setFocusedJobId(params.get("focus") || null);
  }, [location.search]);

  useEffect(() => {
    fetchJobFiles();
  }, [branchFilter]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await Axios.get(
          `${process.env.REACT_APP_API_URL}/api/app/org/branches`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
            },
          }
        );
        if (response.data?.status === "OK" && Array.isArray(response.data?.results)) {
          setBranches(response.data.results);
        } else {
          setBranches([]);
        }
      } catch {
        setBranches([]);
      }
    };
    fetchBranches();
  }, []);

  const fetchJobFiles = async () => {
    try {
      setLoading(true);
      const compId = localStorage.getItem("company");
      const params = new URLSearchParams();
      if (compId) params.set("comp_id", String(compId));
      if (branchFilter !== "all") params.set("branch_id", String(branchFilter));
      const url = `/get-job-files${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await api.get(url);

      if (response.data.status === "OK") {
        setJobs(response.data.result || []);
      } else {
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: response.data.message || "ไม่สามารถดึงข้อมูลแฟ้มงานได้",
        });
        setJobs([]);
      }
    } catch (error) {
      console.error("Error fetching job files:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้",
      });
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const buildJobFileListLink = (focusedId = null) => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (currentPage > 1) params.set("page", String(currentPage));
    if (itemsPerPage !== 100) params.set("limit", String(itemsPerPage));
    if (focusedId != null) params.set("focus", String(focusedId));
    const qs = params.toString();
    return `${location.pathname}${qs ? `?${qs}` : ""}`;
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      
      <main
        className="flex-1 h-screen bg-gray-50 flex flex-col transition-all duration-300"
        style={{
          height: "100vh",
          overflowX: "hidden",
          overflowY: "hidden",
          marginLeft: "var(--sidebar-width)",
        }}
      >
        <Topbar
          breadcrumbs={[
            { label: "งานช่าง", link: "/job-management" },
            { label: "แฟ้มงาน" },
          ]}
        />

        <div className="flex-1 min-h-0 overflow-hidden pl-2 pr-8 pb-6 pt-16">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E13F57]"></div>
              <p className="mt-2 text-gray-600">กำลังโหลดข้อมูล...</p>
            </div>
          ) : (
            <div className="h-full min-h-0">
              <JobFileTable
                fullHeight
                jobs={jobs}
                branches={branches}
                branchFilter={branchFilter}
                onBranchFilterChange={setBranchFilter}
                getJobFileBackLink={(row) => buildJobFileListLink(row?.JobId ?? null)}
                search={search}
                onSearchChange={setSearch}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
                focusedJobId={focusedJobId}
                onFocusJob={(row) => setFocusedJobId(row?.JobId ?? null)}
                onEdit={(row) => {
                  const focusedId = row?.JobId ?? null;
                  setFocusedJobId(focusedId);
                  navigate(`/jobfile/${focusedId}`, {
                    state: {
                      job: row,
                      backLink: buildJobFileListLink(focusedId),
                      backLabel: "แฟ้มงาน",
                    },
                  });
                }}
                onDelete={async (row) => {
                  const result = await Swal.fire({
                    title: "ลบแฟ้มงาน",
                    html: `
                      <div style="text-align: left;">
                        <p style="margin-bottom: 12px;">
                          คุณแน่ใจหรือไม่ว่าต้องการลบแฟ้มงานนี้ ซึ่งข้อมูลต่างๆ ที่เกี่ยวข้องกับงานนี้จะถูกลบไปด้วย?
                        </p>
                        <div style="margin-left: 20px; margin-top: 12px;">
                          <p style="margin: 4px 0;">• สัญญา</p>
                          <p style="margin: 4px 0;">• ใบแจ้งหนี้</p>
                          <p style="margin: 4px 0;">• จ๊อบงาน</p>
                        </div>
                      </div>
                    `,
                    icon: false,
                    showCancelButton: true,
                    confirmButtonColor: "#d33",
                    cancelButtonColor: "#3085d6",
                    confirmButtonText: "ยืนยัน",
                    cancelButtonText: "ยกเลิก",
                  });

                  if (result.isConfirmed) {
                    try {
                      const response = await api.delete(`/delete-contract/${row.JobId}`);
                      
                      if (response.data.status === "OK") {
                        Swal.fire("ลบแล้ว", "ลบแฟ้มงานสำเร็จ", "success");
                        fetchJobFiles();
                      } else {
                        Swal.fire({
                          icon: "error",
                          title: "เกิดข้อผิดพลาด",
                          text: response.data.message || "ไม่สามารถลบแฟ้มงานได้",
                        });
                      }
                    } catch (error) {
                      console.error("Error deleting job file:", error);
                      Swal.fire({
                        icon: "error",
                        title: "เกิดข้อผิดพลาด",
                        text: error.response?.data?.message || "ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้",
                      });
                    }
                  }
                }}
                onView={(row) => {
                  const focusedId = row?.JobId ?? null;
                  setFocusedJobId(focusedId);
                  navigate(`/jobfile/${focusedId}`, {
                    state: {
                      job: row,
                      backLink: buildJobFileListLink(focusedId),
                      backLabel: "แฟ้มงาน",
                    },
                  });
                }}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
